/**
 * Profile 2027 switch (SCO-2710) — TEMPORARY. Old and new Profile / Rankings /
 * Practice (+ their modals and the player login steps) live side by side;
 * every switch point reads PROFILE_2027. Off = what `main` showed before the
 * redesign (the `*Legacy.jsx` copies).
 *
 * Per device, no backend: the choice is saved in localStorage by the settings
 * form row, or by `?profile2027=1|0` on the URL. Read ONCE at load, so a page
 * is never half old, half new — changing it needs a reload.
 *
 * Keep this module import-light: index.jsx reads it during boot.
 */
import { readStoredJson, writeStoredJson } from "../functions/safeStorage";

// What a device with no saved choice gets: off, like every 2027 section (SCO-3065).
export const PROFILE_2027_DEFAULT = false;

// null = per-device choice applies. true / false beats every saved choice and
// hides the settings row: the one-line emergency rollback (false).
export const PROFILE_2027_FORCE = null;

// Who sees the row in the settings forms: "staff" (allAccessUsers) |
// "everyone" (Phil 2026-10-20) | "nobody". Staff only while prod runs ahead of the store apps (Phil 2026-10-28).
export const PROFILE_2027_TOGGLE_FOR = PROFILE_2027_FORCE === null ? "staff" : "nobody";

const KEY = "sh27Profile2027";

function readChoice() {
  if (PROFILE_2027_FORCE !== null) return PROFILE_2027_FORCE;
  try {
    const url = new URL(window.location.href);
    const param = url.searchParams.get("profile2027");
    if (param === "1" || param === "0") {
      writeStoredJson(KEY, param === "1");
      // Dropped so a later reload (the settings toggle) doesn't re-apply it.
      url.searchParams.delete("profile2027");
      window.history.replaceState(window.history.state, "", url.toString());
      return param === "1";
    }
  } catch (err) {
    /* no URL to read */
  }
  const saved = readStoredJson(KEY, null);
  return typeof saved === "boolean" ? saved : PROFILE_2027_DEFAULT;
}

export const PROFILE_2027 = readChoice();

// The saved choice now, which another webview may have changed since load.
export function savedProfile2027() {
  if (PROFILE_2027_FORCE !== null) return PROFILE_2027_FORCE;
  const saved = readStoredJson(KEY, null);
  return typeof saved === "boolean" ? saved : PROFILE_2027_DEFAULT;
}

// Saves only; the caller reloads the page.
export function saveProfile2027(on) {
  return writeStoredJson(KEY, !!on);
}

const RELOAD_KEY = "sh27Profile2027Reload";

// Reloads this page when the saved choice no longer matches the version it
// loaded: the native Settings sheet is another webview, and the profile's
// reload on sheet close can read storage before that write lands.
export function followProfile2027Choice() {
  if (PROFILE_2027_FORCE !== null) return;
  // The settings page writes the choice itself; it never needs to follow it.
  if (window.location.pathname.endsWith("/player/settings")) return;
  const check = () => {
    const saved = readStoredJson(KEY, null);
    if (typeof saved !== "boolean" || saved === PROFILE_2027) return;
    try {
      // At most one follow-reload per 5s: a storage read that keeps lagging can't loop.
      const last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
      if (Date.now() - last < 5000) return;
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch (err) {
      return;
    }
    window.location.reload();
  };
  window.addEventListener("storage", (e) => {
    if (e.key === KEY) check();
  });
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) check();
  });
  window.addEventListener("focus", check);
  window.addEventListener("pageshow", check);
  // Covers a reload that read the old value just before the sheet's write landed.
  setTimeout(check, 1500);
}
